import React, { useState, useEffect, createContext, useContext, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "./hooks/useUser";
import Navbar from "./components/Navbar";
import AuthPage from "./pages/AuthPage";
import HomePage from "./pages/HomePage";
import GuessSpellPage from "./games/spells/GuessSpellPage";
import GuessLocationPage from "./games/location/GuessLocationPage";
import GuessNpcPage from "./games/npc/GuessNpcPage";
import GuessLorePage from "./games/lore/GuessLorePage";
import ProfilePage from "./pages/ProfilePage";
import LeaderboardPage from "./pages/LeaderboardPage";
import FeedbackPage from "./pages/FeedbackPage";
import PageTransition from "./components/PageTransition";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Footer from "./components/Footer";

// ── Error Boundary — catches runtime errors instead of white screen ──
class ErrorBoundary extends Component {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", minHeight: "100vh", color: "#c4a96a",
          fontFamily: "sans-serif", textAlign: "center", padding: "2rem",
          background: "rgb(12, 10, 7)",
        }}>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Something went wrong</h1>
          <p style={{ color: "#8a8074", marginBottom: "1.5rem" }}>
            An unexpected error occurred. Please try refreshing the page.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.href = "/" }}
            style={{
              padding: "0.6rem 1.5rem", background: "linear-gradient(135deg, #b8860b, #d4a44c)",
              color: "#1a1408", border: "none", borderRadius: "6px", cursor: "pointer",
              fontWeight: 600, fontSize: "0.95rem",
            }}
          >
            Back to Home
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ── Auth Modal Context ──
export const AuthModalContext = createContext({ open: () => {}, close: () => {} });
export const useAuthModal = () => useContext(AuthModalContext);

function PrivateRoute({ children }) {
  const { user, loading } = useUser();
  const { open } = useAuthModal();

  if (loading) return null;
  if (!user) {
    setTimeout(() => open(), 0);
    return null;
  }
  return children;
}

// ── Detects ?confirmed=true after email link click ──
function ConfirmationHandler({ onConfirmed, onEmailChanged, onLinkError }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("confirmed") === "true") {
      onConfirmed();
      navigate("/", { replace: true });
    }

    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.replace('#', '?'));
    const message = hashParams.get('message');
    const error = hashParams.get('error');
    const errorDesc = hashParams.get('error_description');

    if (message && message.toLowerCase().includes('confirmation link accepted')) {
      onEmailChanged();
      window.history.replaceState(null, '', '/');
    } else if (error && errorDesc) {
      onLinkError(errorDesc.replace(/\+/g, ' '));
      window.history.replaceState(null, '', '/');
    }
  }, []);

  return null;
}

function AppContent({ onOpenAuth, onConfirmed, onEmailChanged, onLinkError }) {
  return (
    <>
      <ConfirmationHandler onConfirmed={onConfirmed} onEmailChanged={onEmailChanged} onLinkError={onLinkError} />
      <Navbar onOpenAuth={onOpenAuth} />
      <PageTransition>
        <Routes>
          <Route path="/" element={<HomePage />} />

          {/* ── Game routes ── */}
          <Route path="/guess-spell"    element={<GuessSpellPage />} />
          <Route path="/guess-location" element={<GuessLocationPage />} />
          <Route path="/guess-npc"      element={<GuessNpcPage />} />
          <Route path="/guess-lore"     element={<GuessLorePage />} />

          {/* ── Other routes ── */}
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/feedback"    element={<FeedbackPage />} />

          <Route path="/profile"           element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/profile/:username" element={<ProfilePage />} />
          <Route path="/login"             element={<Navigate to="/" replace />} />
          <Route path="/terms"             element={<TermsPage />} />
          <Route path="/privacy"           element={<PrivacyPage />} />
          <Route path="/reset-password"    element={<ResetPasswordPage />} />
        </Routes>
      </PageTransition>
      <Footer />
    </>
  );
}

function App() {
  const [authOpen, setAuthOpen]               = useState(false);
  const [confirmedOpen, setConfirmedOpen]     = useState(false);
  const [emailChangedOpen, setEmailChangedOpen] = useState(false);
  const [linkErrorMsg, setLinkErrorMsg]       = useState(null);

  const authModal = {
    open:  () => setAuthOpen(true),
    close: () => setAuthOpen(false),
  };

  return (
    <AuthModalContext.Provider value={authModal}>
      <div style={{ position: 'relative', minHeight: '100vh' }}>
        <div style={{
          position: 'fixed', inset: 0, zIndex: 0,
        }} />
        <div style={{
          position: 'fixed', inset: 0, zIndex: 0,
        }} />
        <BrowserRouter>
          <ErrorBoundary>
            <AppContent onOpenAuth={authModal.open} onConfirmed={() => setConfirmedOpen(true)} onEmailChanged={() => setEmailChangedOpen(true)} onLinkError={(msg) => setLinkErrorMsg(msg)} />
          </ErrorBoundary>
        </BrowserRouter>

        {/* ── Auth Modal ── */}
        {authOpen && (
          <AuthPage
            isModal
            onClose={authModal.close}
            onAuthSuccess={authModal.close}
          />
        )}

        {/* ── Email Confirmed Modal ── */}
        {confirmedOpen && (
          <div className="auth-modal-backdrop" onClick={() => setConfirmedOpen(false)}>
            <div className="auth-card confirmed-card" onClick={e => e.stopPropagation()}>
              <button className="auth-close-btn" onClick={() => setConfirmedOpen(false)}>✕</button>
              <div className="confirmed-icon">✔</div>
              <h2 className="confirmed-title">Email Confirmed!</h2>
              <p className="confirmed-text">Your account is ready.</p>
              <p className="confirmed-text">Log in to begin your adventure.</p>
              <button
                className="btn-gold"
                style={{ width: '60%', marginTop: '1.5rem' }}
                onClick={() => { setConfirmedOpen(false); authModal.open(); }}
              >
                Log In
              </button>
            </div>
          </div>
        )}

        {/* ── Link Error Modal ── */}
        {linkErrorMsg && (
          <div className="auth-modal-backdrop" onClick={() => setLinkErrorMsg(null)}>
            <div className="auth-card confirmed-card" onClick={e => e.stopPropagation()}>
              <button className="auth-close-btn" onClick={() => setLinkErrorMsg(null)}>✕</button>
              <div className="confirmed-icon" style={{ background: 'var(--error-bg)', borderColor: 'rgba(168,50,50,0.4)', color: 'var(--error-text)' }}>⚠</div>
              <h2 className="confirmed-title" style={{ background: 'none', WebkitTextFillColor: 'var(--error-text)', color: 'var(--error-text)' }}>Link Expired</h2>
              <p className="confirmed-text">{linkErrorMsg}</p>
              <button
                className="btn-gold"
                style={{ width: '60%', marginTop: '1.5rem' }}
                onClick={() => setLinkErrorMsg(null)}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* ── Email Changed Modal ── */}
        {emailChangedOpen && (
          <div className="auth-modal-backdrop" onClick={() => setEmailChangedOpen(false)}>
            <div className="auth-card confirmed-card" onClick={e => e.stopPropagation()}>
              <button className="auth-close-btn" onClick={() => setEmailChangedOpen(false)}>✕</button>
              <div className="confirmed-icon">✔</div>
              <h2 className="confirmed-title">Email Updated!</h2>
              <p className="confirmed-text">Your email address has been successfully changed.</p>
              <button
                className="btn-gold"
                style={{ width: '60%', marginTop: '1.5rem' }}
                onClick={() => setEmailChangedOpen(false)}
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </AuthModalContext.Provider>
  );
}

export default App;